import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

interface JsonViewerProps {
  data: any;
  title?: string;
  defaultExpanded?: boolean;
  maxDepth?: number;
}

interface JsonNodeProps {
  data: any;
  keyName?: string;
  depth?: number;
  maxDepth?: number;
}

const JsonNode: React.FC<JsonNodeProps> = ({ data, keyName, depth = 0, maxDepth = 3 }) => {
  const [isExpanded, setIsExpanded] = useState(depth < 2);
  const isArray = Array.isArray(data);
  const isObject = data !== null && typeof data === 'object' && !Array.isArray(data);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderValue = (value: any): React.ReactNode => {
    if (value === null) return <span className="text-gray-500">null</span>;
    if (value === undefined) return <span className="text-gray-500">undefined</span>;
    if (typeof value === 'string') return <span className="text-green-600">"{value}"</span>;
    if (typeof value === 'number') return <span className="text-blue-600">{value}</span>;
    if (typeof value === 'boolean') return <span className="text-purple-600">{value.toString()}</span>;
    if (Array.isArray(value)) {
      return (
        <span className="text-gray-600">
          Array({value.length})
        </span>
      );
    }
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      return (
        <span className="text-gray-600">
          Object({keys.length})
        </span>
      );
    }
    return <span className="text-gray-600">{String(value)}</span>;
  };

  const renderKey = () => {
    if (keyName === undefined) return null;
    return (
      <span className="text-purple-600 font-medium">
        "{keyName}":
      </span>
    );
  };

  if (!isObject && !isArray) {
    return (
      <div className="flex items-center space-x-1">
        {renderKey()}
        {renderValue(data)}
      </div>
    );
  }

  const keys = isArray ? Array.from({ length: data.length }, (_, i) => i.toString()) : Object.keys(data);
  const hasChildren = keys.length > 0;

  if (depth >= maxDepth || !hasChildren) {
    return (
      <div className="flex items-center space-x-1">
        {renderKey()}
        {renderValue(data)}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      <div className="flex items-center space-x-1">
        <button
          onClick={toggleExpanded}
          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="h-2.5 w-2.5 text-gray-500" />
          ) : (
            <ChevronRight className="h-2.5 w-2.5 text-gray-500" />
          )}
        </button>
        {renderKey()}
        <span className="text-gray-600">
          {isArray ? '[' : '{'}
        </span>
        {!isExpanded && (
          <>
            <span className="text-gray-500">...</span>
            <span className="text-gray-600">
              {isArray ? ']' : '}'}
            </span>
          </>
        )}
      </div>
      
      {isExpanded && (
        <div className="ml-4 space-y-0.5">
          {keys.map((key, index) => (
            <div key={key} className="border-l border-gray-200 pl-2">
              <div className="flex items-center">
                <JsonNode
                  data={data[key]}
                  keyName={key}
                  depth={depth + 1}
                  maxDepth={maxDepth}
                />
                {index < keys.length - 1 && (
                  <span className="text-gray-400 ml-1">,</span>
                )}
              </div>
            </div>
          ))}
          <div className="text-gray-600">
            {isArray ? ']' : '}'}
          </div>
        </div>
      )}
    </div>
  );
};

const JsonViewer: React.FC<JsonViewerProps> = ({ 
  data, 
  title = "JSON Data", 
  defaultExpanded = false,
  maxDepth = 3 
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
        <details open={isExpanded} className="group">
          <summary 
            className="cursor-pointer flex items-center justify-between text-xs font-medium text-gray-900 hover:text-gray-700"
            onClick={(e) => {
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }}
          >
            <div className="flex items-center space-x-2">
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              <span>{title}</span>
              <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                {typeof data === 'object' && data !== null ? 
                  (Array.isArray(data) ? `${data.length} items` : `${Object.keys(data).length} keys`) : 
                  '1 item'
                }
              </span>
            </div>
          </summary>
          
          <div className="mt-3 bg-white p-2 rounded border">
            <div className="text-xs font-mono leading-tight">
              <JsonNode data={data} maxDepth={maxDepth} />
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};

export default JsonViewer; 